import {
  PREFIX_USES,
  SUBJECT_ACTIVITY_ID,
  SUBJECT_ACTIVITY_NAME,
} from "../constants/kpiDependencyConflictConstants";
import type { KpiDependencyConflictSubject } from "../types/kpiDependencyConflict";
import type { KpiDependent, RemovedActivityRef } from "../types/kpi";
import { formatKpiUsesLine } from "./kpiConflictFromError";

export type TemplateActivityRemovalKpiBullet = {
  kpiId: number;
  kpiName: string;
  usesLine: string | null;
};

export type TemplateActivityRemovalBlock = {
  removedActivity: RemovedActivityRef;
  kpis: TemplateActivityRemovalKpiBullet[];
};

function compareActivityNames(a: RemovedActivityRef, b: RemovedActivityRef): number {
  const nameCmp = a.activity_name.localeCompare(b.activity_name, undefined, {
    sensitivity: "accent",
  });
  if (nameCmp !== 0) return nameCmp;
  return a.activity_id - b.activity_id;
}

function compareKpiNames(a: TemplateActivityRemovalKpiBullet, b: TemplateActivityRemovalKpiBullet): number {
  const nameCmp = a.kpiName.localeCompare(b.kpiName, undefined, { sensitivity: "accent" });
  if (nameCmp !== 0) return nameCmp;
  return a.kpiId - b.kpiId;
}

function mergeDependsOnByActivityId(rows: RemovedActivityRef[]): RemovedActivityRef[] {
  const byId = new Map<number, RemovedActivityRef>();
  for (const row of rows) {
    const existing = byId.get(row.activity_id);
    if (!existing) {
      byId.set(row.activity_id, { ...row });
      continue;
    }
    if (!existing.activity_name.trim() && row.activity_name.trim()) {
      byId.set(row.activity_id, { activity_id: row.activity_id, activity_name: row.activity_name });
    }
  }
  return Array.from(byId.values());
}

function usesActivityNamesFromDependsOn(dependsOn: RemovedActivityRef[]): string[] {
  return dependsOn
    .map((r) => r.activity_name.trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "accent" }));
}

function unionDependsOnForDependents(dependents: KpiDependent[]): RemovedActivityRef[] {
  const merged: RemovedActivityRef[] = [];
  for (const dep of dependents) {
    merged.push(...(dep.depends_on_activities ?? []));
  }
  return mergeDependsOnByActivityId(merged);
}

function collectRemovedActivityRefs(dependents: KpiDependent[]): RemovedActivityRef[] {
  const byId = new Map<number, RemovedActivityRef>();
  for (const dep of dependents) {
    for (const ref of dep.removed_activities ?? []) {
      const existing = byId.get(ref.activity_id);
      if (!existing) {
        byId.set(ref.activity_id, { ...ref });
        continue;
      }
      if (!existing.activity_name.trim() && ref.activity_name.trim()) {
        byId.set(ref.activity_id, { activity_id: ref.activity_id, activity_name: ref.activity_name });
      }
    }
  }
  return Array.from(byId.values()).sort(compareActivityNames);
}

export function listNamedRemovedActivitiesForFallback(
  dependents: KpiDependent[],
  subject?: KpiDependencyConflictSubject
): RemovedActivityRef[] {
  const refs = collectRemovedActivityRefs(dependents);

  const subjectActivityId = subject?.[SUBJECT_ACTIVITY_ID];
  const subjectActivityName = subject?.[SUBJECT_ACTIVITY_NAME];
  if (
    (typeof subjectActivityId === "number" || typeof subjectActivityId === "string") &&
    typeof subjectActivityName === "string" &&
    subjectActivityName.trim()
  ) {
    const id = Number(subjectActivityId);
    const existing = refs.find((r) => r.activity_id === id);
    if (!existing) {
      refs.push({ activity_id: id, activity_name: subjectActivityName.trim() });
    } else if (!existing.activity_name.trim()) {
      existing.activity_name = subjectActivityName.trim();
    }
  }

  return refs
    .filter((r) => r.activity_name.trim())
    .sort(compareActivityNames);
}

export function groupTemplateActivityRemovalBlocks(
  dependents: KpiDependent[]
): TemplateActivityRemovalBlock[] {
  const removedRefs = collectRemovedActivityRefs(dependents);
  if (removedRefs.length === 0) {
    return [];
  }

  const blocks: TemplateActivityRemovalBlock[] = [];

  for (const removedActivity of removedRefs) {
    const matching = dependents.filter((dep) =>
      (dep.removed_activities ?? []).some((r) => r.activity_id === removedActivity.activity_id)
    );
    if (matching.length === 0) {
      continue;
    }

    const byKpiId = new Map<number, KpiDependent[]>();
    for (const dep of matching) {
      const list = byKpiId.get(dep.kpi_id) ?? [];
      list.push(dep);
      byKpiId.set(dep.kpi_id, list);
    }

    const kpis: TemplateActivityRemovalKpiBullet[] = [];
    for (const [kpiId, rows] of byKpiId.entries()) {
      const kpiName = rows[0]?.kpi_name?.trim() ? rows[0].kpi_name : `KPI #${kpiId}`;
      const unioned = unionDependsOnForDependents(rows);
      const names = usesActivityNamesFromDependsOn(unioned);
      const usesText = formatKpiUsesLine(names);
      kpis.push({
        kpiId,
        kpiName,
        usesLine: usesText ? `${PREFIX_USES}${usesText}` : null,
      });
    }

    kpis.sort(compareKpiNames);

    if (kpis.length === 0) {
      continue;
    }

    blocks.push({
      removedActivity,
      kpis,
    });
  }

  return blocks;
}
